import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog';

import {Observable, Subscription} from 'rxjs';

import {AccountCreationBatchOutcomeEnum} from '@spa/api';

import {Helper} from 'appShared/classes';
import {DialogButtonTypesEnum, DialogComponent, DialogConfigInterface} from 'appShared/components';

import {BatchAccountCreationRow, BatchAccountCreationState} from './batch-account-creation-result.state';

export interface BatchAccountCreationResultDialogData {
    caption: string;
    state$: Observable<BatchAccountCreationState>;
}

@Component({
    selector: 'app-batch-account-creation-result',
    templateUrl: './batch-account-creation-result.component.html',
    styleUrls: ['./batch-account-creation-result.component.scss']
})
export class BatchAccountCreationResultComponent
    extends DialogComponent<BatchAccountCreationResultComponent> implements OnInit, OnDestroy {

    private copyTimeout: ReturnType<typeof setTimeout>;
    private stateSubscription: Subscription;

    public state: BatchAccountCreationState = {
        total: 0,
        inProgress: true,
        succeeded: false,
        rows: [],
        summaryText: ''
    };
    public copied = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: BatchAccountCreationResultDialogData,
        public dialogRef: MatDialogRef<BatchAccountCreationResultComponent>,
    ) {
        super(dialogRef);

        this.config = this.buildConfig();
    }

    private applyInProgress(inProgress: boolean): void {
        this.dialogRef.disableClose = inProgress;
        this.config.disabledCloseBtn = inProgress;
        this.config.buttonsHidden = inProgress;
    }

    private buildConfig(): DialogConfigInterface {
        return {
            process: {loading: false, finished: false, failed: false, succeedMessage: '', failedMessage: ''},
            buttons: [
                {
                    type: DialogButtonTypesEnum.Ok,
                    customText: 'Close',
                    disabled: false,
                    hidden: false
                }
            ],
            buttonsDisabled: false,
            buttonsHidden: true,
            disabledCloseBtn: true
        };
    }

    ngOnInit(): void {
        super.ngOnInit();

        this.stateSubscription = this.data.state$.subscribe((state: BatchAccountCreationState) => {
            this.state = state;
            this.applyInProgress(state.inProgress);
        });
    }

    ngOnDestroy(): void {
        if (this.stateSubscription) {
            this.stateSubscription.unsubscribe();
        }

        if (this.copyTimeout) {
            clearTimeout(this.copyTimeout);
        }
    }

    get createdAccountNumbers(): string[] {
        return (this.state.rows || [])
            .filter((row: BatchAccountCreationRow) => !!row.accountReferenceId)
            .map((row: BatchAccountCreationRow) => row.accountReferenceId);
    }

    getOutcomeName(outcome: AccountCreationBatchOutcomeEnum): string {
        return Helper.textsHelper().getAccountCreationBatchOutcomeName(outcome) ?? '-';
    }

    getOutcomeClass(outcome: AccountCreationBatchOutcomeEnum): string {
        switch (outcome) {
            case AccountCreationBatchOutcomeEnum.CreatedActivated:
                return 'text-accent';
            case AccountCreationBatchOutcomeEnum.CreatedActivationNotInitiated:
                return 'text-warning';
            default:
                return 'text-danger';
        }
    }

    onActionButtonClicked(): void {
        // The request cannot be cancelled once sent, so the dialog stays open until it returns.
        if (this.state.inProgress) {
            return;
        }

        // Once created the accounts exist whichever way the dialog is dismissed.
        this.dialogRef.close(this.state.succeeded);
    }

    copyAccountNumbers(): void {
        const numbers = this.createdAccountNumbers.join('\n');

        if (!numbers || !navigator.clipboard) {
            return;
        }

        navigator.clipboard.writeText(numbers).then(() => {
            this.copied = true;

            if (this.copyTimeout) {
                clearTimeout(this.copyTimeout);
            }

            this.copyTimeout = setTimeout(() => {
                this.copied = false;
            }, 2000);
        }).catch(() => {
            // Clipboard access denied by the browser; the numbers stay visible in the table.
            this.copied = false;
        });
    }
}
